import React, { useState, useCallback } from 'react';
import HBTooltip, { ITooltipProps } from '../HBTooltip/HBTooltip';
import type { TMenuItem } from '../HBMenu/HBMenuItemOld.types';
import HBMenu from '../HBMenu/HBMenuOld';
import type { IMenuProps } from '../HBMenu/HBMenuOld';
import type { IButtonProps } from './HBButton';
import {
    MenuWrapper,
    SplitButton,
    SplitButtonArrowIcon,
    SplitTextContainer
} from './HBSplitButton.styles';
import { recursivelyWrapStringChildrenWithSpans } from './HBButton.utils';
import { IconProps } from '@honeybook/icons';
import {
    HBPopoverOnOpenChangeTrigger,
    IOnOpenChange
} from '../HBPopover/HBPopover.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';

export interface ISplitButtonProps extends IButtonProps {
    /**
     * Configuration for the dropdown half of the split button.
     * `menuItems` populates the dropdown; `isMainButtonDisabled` / `isMenuButtonDisabled`
     * disable each half independently; `mainButtonTooltipProps` adds a tooltip explaining
     * a disabled main action (e.g. "Invoice must be finalized before sending").
     */
    splitButtonProps: Omit<IMenuProps, 'target' | 'menuItems'> & {
        isMainButtonDisabled?: boolean;
        isMenuButtonDisabled?: boolean;
        mainButtonTooltipProps?: Omit<ITooltipProps, 'target' | 'children'> & {
            content: ITooltipProps['children'];
        };
        menuItems?: TMenuItem[];
        onClick?: () => void;
        icon?: (props: IconProps) => JSX.Element;
    };
}

const HBSplitButton = ({
    splitButtonProps,
    children,
    size = 'md',
    scope = 'system',
    onClick,
    buttonTheme = 'primary',
    margin,
    disabled,
    ...buttonProps
}: ISplitButtonProps) => {
    const {
        isMainButtonDisabled,
        isMenuButtonDisabled,
        onOpenChange,
        menuItems,
        placement,
        mainButtonTooltipProps,
        onClick: onSplitButtonClick,
        icon: SplitButtonIcon = SplitButtonArrowIcon,
        ..._splitButtonProps
    } = splitButtonProps || {};

    const { content, ..._mainButtonTooltipProps } =
        mainButtonTooltipProps || {};

    const [isSplitMenuOpen, setIsSplitMenuOpen] = useState(false);

    const _onOpenChange = useCallback(
        ({ isOpen, triggeredFrom }: IOnOpenChange) => {
            if (!menuItems) {
                return;
            }
            setIsSplitMenuOpen(isOpen);
            onOpenChange?.({ isOpen });
            if (triggeredFrom !== HBPopoverOnOpenChangeTrigger.ClickOutside) {
                onSplitButtonClick?.();
            }
        },
        [onOpenChange, onSplitButtonClick, menuItems]
    );

    const mappedMenuItems = menuItems?.map<TMenuItem>(item => {
        if ('onClick' in item) {
            return {
                ...item,
                onClick: e => {
                    e.stopPropagation();
                    item.onClick(e);
                }
            };
        }
        return item;
    });

    const wrappedChildren = recursivelyWrapStringChildrenWithSpans(children);

    const menuJSX = (
        <HBMenu
            withArrow
            shouldSupportOverflowHidden
            stopPropagationOnTargetClick={!!menuItems}
            customStyle={{ zIndex: 100000000 }}
            placement={placement}
            onOpenChange={_onOpenChange}
            menuItems={mappedMenuItems}
            forceClose={!menuItems}
            target={<SplitButtonIcon />}
            {..._splitButtonProps}
        />
    );

    const buttonTestId = useBuildTestIdInternal({
        testId: buttonProps.testId,
        identifier: buttonProps.testIdName || buttonProps.id,
        suffix: 'button',
        componentName: 'HBButton',
        testIdType: buttonProps.testIdType
    });

    return (
        <SplitButton
            {...buttonProps}
            isSplitMenuOpen={isSplitMenuOpen}
            isSplitMenuTop={placement === 'top'}
            onClick={isMainButtonDisabled ? undefined : onClick}
            // need to solve this issue of this being a styled component
            theme={buttonTheme}
            disabled={disabled}
            isMainButtonDisabled={disabled || isMainButtonDisabled}
            isMenuButtonDisabled={disabled || isMenuButtonDisabled}
            // Had to add these two due to a TS issue
            // SplitButton extends the Button styled component but it doesn't get the default values
            // assigned in HBButton
            size={size}
            scope={scope}
            $margin={margin}
            data-testid={buttonTestId}
        >
            <HBTooltip
                {..._mainButtonTooltipProps}
                target={
                    <SplitTextContainer>{wrappedChildren}</SplitTextContainer>
                }
            >
                {content}
            </HBTooltip>
            {menuItems ? (
                menuJSX
            ) : (
                <MenuWrapper
                    isDisabled={disabled || isMenuButtonDisabled}
                    onClick={e => {
                        e.stopPropagation();
                        onSplitButtonClick?.();
                    }}
                >
                    {menuJSX}
                </MenuWrapper>
            )}
        </SplitButton>
    );
};

export default HBSplitButton;
